/** Shared flip-clock digit state for Challenges countdowns (canvas + DOM). */

export type FlipCell = {
  /** Settled character (after flip completes). */
  char: string;
  from: string;
  to: string;
  /** 0 = start of flip, 1 = idle. */
  progress: number;
};

export const FLIP_DURATION_SEC = 0.32;

export function createFlipCountdown(label: string): FlipCell[] {
  return Array.from(label).map((ch) => ({
    char: ch,
    from: ch,
    to: ch,
    progress: 1,
  }));
}

/** Sync cells to a new label; digits that change start a flip. Returns true if any digit flipped. */
export function syncFlipCountdown(cells: FlipCell[], label: string): boolean {
  let flipped = false;
  const chars = Array.from(label);
  while (cells.length < chars.length) {
    const ch = chars[cells.length] ?? "0";
    cells.push({ char: ch, from: ch, to: ch, progress: 1 });
  }
  if (cells.length > chars.length) cells.length = chars.length;
  for (let i = 0; i < chars.length; i++) {
    const next = chars[i]!;
    const cell = cells[i]!;
    if (next === ":") {
      cell.char = ":";
      cell.from = ":";
      cell.to = ":";
      cell.progress = 1;
      continue;
    }
    if (cell.to !== next) {
      cell.from = cell.progress < 1 ? cell.from : cell.to;
      cell.to = next;
      cell.progress = 0;
      flipped = true;
    }
  }
  return flipped;
}

export function advanceFlipCountdown(cells: FlipCell[], dt: number) {
  const step = dt / FLIP_DURATION_SEC;
  for (const cell of cells) {
    if (cell.progress >= 1) continue;
    cell.progress = Math.min(1, cell.progress + step);
    if (cell.progress >= 1) cell.char = cell.to;
  }
}

function easeInCubic(t: number) {
  return t * t * t;
}

function easeOutCubic(t: number) {
  const u = 1 - t;
  return 1 - u * u * u;
}

/** Draw flip-clock label centered at (cx, cy). */
export function drawFlipCountdown(
  ctx: CanvasRenderingContext2D,
  cells: FlipCell[],
  cx: number,
  cy: number,
  fontSize: number,
  color: string,
  maxWidth: number,
  alpha = 1,
) {
  if (!cells.length) return;
  ctx.save();
  ctx.font = `bold ${fontSize}px "Segoe UI", system-ui, sans-serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillStyle = color;

  const gap = fontSize * 0.06;
  const widths = cells.map((c) => {
    const ch = c.progress < 1 ? c.to : c.char;
    return Math.max(ctx.measureText(ch === ":" ? ":" : "0").width, fontSize * (ch === ":" ? 0.28 : 0.58));
  });
  let total = widths.reduce((a, b) => a + b, 0) + gap * Math.max(0, cells.length - 1);
  const scale = total > maxWidth && total > 0 ? maxWidth / total : 1;
  total *= scale;
  const cellH = fontSize * 1.2 * scale;
  const fs = fontSize * scale;

  let x = cx - total / 2;
  ctx.font = `bold ${fs}px "Segoe UI", system-ui, sans-serif`;

  for (let i = 0; i < cells.length; i++) {
    const cell = cells[i]!;
    const w = widths[i]! * scale;
    const digitCx = x + w / 2;
    x += w + gap * scale;

    if (cell.to === ":") {
      ctx.globalAlpha = alpha;
      ctx.fillText(":", digitCx, cy);
      continue;
    }

    const t = cell.progress;
    if (t >= 1) {
      ctx.globalAlpha = alpha;
      ctx.fillText(cell.to, digitCx, cy);
      continue;
    }

    ctx.save();
    ctx.beginPath();
    ctx.rect(digitCx - w * 0.55, cy - cellH * 0.55, w * 1.1, cellH * 1.1);
    ctx.clip();

    // Old digit drops downward.
    const outY = cy + easeInCubic(t) * cellH;
    ctx.globalAlpha = alpha * (1 - t);
    ctx.fillText(cell.from, digitCx, outY);

    // New digit drops in from above.
    const inY = cy - (1 - easeOutCubic(t)) * cellH;
    ctx.globalAlpha = alpha * Math.min(1, t * 1.15);
    ctx.fillText(cell.to, digitCx, inY);
    ctx.restore();
  }
  ctx.restore();
}

/** Ensure DOM flip-clock children exist and match label length. */
export function ensureDomFlipClock(root: HTMLElement, label: string) {
  const chars = Array.from(label);
  if (root.dataset.flipReady === "1" && root.children.length === chars.length) return;
  root.textContent = "";
  root.classList.add("challengeFlipClock");
  root.dataset.flipReady = "1";
  for (const ch of chars) {
    if (ch === ":") {
      const colon = document.createElement("span");
      colon.className = "challengeFlipColon";
      colon.textContent = ":";
      root.appendChild(colon);
    } else {
      const wrap = document.createElement("span");
      wrap.className = "challengeFlipDigit";
      wrap.dataset.digit = ch;
      const card = document.createElement("span");
      card.className = "challengeFlipCard";
      const out = document.createElement("span");
      out.className = "challengeFlipOut";
      out.textContent = ch;
      const inn = document.createElement("span");
      inn.className = "challengeFlipIn";
      inn.textContent = ch;
      card.append(out, inn);
      wrap.appendChild(card);
      root.appendChild(wrap);
    }
  }
}

/** Apply flip-cell state onto an existing DOM flip clock. */
export function applyDomFlipClock(root: HTMLElement, cells: FlipCell[]) {
  ensureDomFlipClock(root, cells.map((c) => c.to).join(""));
  for (let i = 0; i < root.children.length; i++) {
    const el = root.children[i] as HTMLElement;
    const cell = cells[i];
    if (!cell || el.classList.contains("challengeFlipColon") || cell.to === ":") continue;
    const out = el.querySelector(".challengeFlipOut") as HTMLElement | null;
    const inn = el.querySelector(".challengeFlipIn") as HTMLElement | null;
    if (!out || !inn) continue;
    if (cell.progress < 1) {
      out.textContent = cell.from;
      inn.textContent = cell.to;
      if (el.dataset.animFrom !== cell.from || el.dataset.animTo !== cell.to) {
        el.dataset.animFrom = cell.from;
        el.dataset.animTo = cell.to;
        el.classList.remove("is-flipping");
        void el.offsetWidth;
        el.classList.add("is-flipping");
      } else if (!el.classList.contains("is-flipping")) {
        el.classList.add("is-flipping");
      }
    } else {
      out.textContent = cell.to;
      inn.textContent = cell.to;
      el.classList.remove("is-flipping");
      el.dataset.digit = cell.to;
      el.dataset.animFrom = cell.to;
      el.dataset.animTo = cell.to;
    }
  }
}
